import React, { useState } from 'react';
import styles from './ManagerCalendarNotifications.module.scss';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import CheckboxField from '../../../../../../Components/Form/components/CheckboxField';
import Text from '../../../../../../Components/Base/Text/Text';
import RadioListField from '../../../../../../Components/Form/components/RadioListField';
import TimePickerField from '../../../../../../Components/Form/components/TimePickerField/TimePickerField';
import { useManagerContext } from '../../../../Services/ManagerContext';
import useCustomSettings from '../../../../../Base/Hooks/useCustomSettings';
import { CustomSettingKey } from '../../../../../Base/Enums/CustomSettingKey.enum';
import { useEffectAsync } from '../../../../../../helpers';
import Loading from '../../../../../../Components/Loading';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';
import NumberField from '../../../../../../Components/Form/components/NumberField';
import FloatingSubmitButton from '../../../../../Base/Components/FloatingSubmitButton/FloatingSubmitButton';
import { getMomentDate } from '../../../../../../Utils/dates.util';
import CustomSettingsTableItem from '../../../../../CustomSettings/Components/CustomSettingsTable/CustomSettingsTableItem';
import CustomSettingsTable from '../../../../../CustomSettings/Components/CustomSettingsTable/CustomSettingsTable';

const RADIO_OPTIONS = [
	{
		title: 'base.yes',
		value: true,
	},
	{
		title: 'base.no',
		value: false,
	},
];

interface ManagerCalendarNotificationsValues {
	email: boolean;
	notifications: boolean;
	notificationsDaysBefore: string;
	notificationsInTime: string;
	requireTermConfirmation: boolean;
	sms: boolean;
}

const ManagerCalendarNotifications: React.FC = () => {
	const [isLoading, setIsLoading] = useState(false);
	const { workplaceId } = useManagerContext();
	const { getCustomSettings, saveCustomSettings } =
		useCustomSettings(workplaceId);
	const formMethods = useForm<ManagerCalendarNotificationsValues>({});
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const handleLoad = async () => {
		const data = await getCustomSettings(
			CustomSettingKey.CALENDAR_NOTIFICATIONS_SETTINGS,
		);
		if (data) {
			formMethods.reset(data);
		}
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		await handleLoad();
		setIsLoading(false);
	}, []);

	const handleSubmit = async (values: ManagerCalendarNotificationsValues) => {
		setIsLoading(true);
		const { notificationsDaysBefore, notificationsInTime, ...restOfValues } =
			values;
		const response = await saveCustomSettings(
			CustomSettingKey.CALENDAR_NOTIFICATIONS_SETTINGS,
			{
				...restOfValues,
				notificationsDaysBefore: parseInt(notificationsDaysBefore),
				notificationsInTime: getMomentDate(notificationsInTime).format(
					'YYYY-MM-DDTHH:mm:ssZ',
				),
			},
		);
		if (response?.status === 200) {
			showNotificationSuccess({ content: t('base.saved') });
			await handleLoad();
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<CustomSettingsTable>
					<CustomSettingsTableItem label='component.managerCalendarNotifications.nextTermNotification'>
						<div className={styles.inline}>
							<CheckboxField
								name='notifications'
								tx='component.managerCalendarNotifications.notifications'
							/>
							<CheckboxField
								name='email'
								tx='component.managerCalendarNotifications.email'
							/>
							<CheckboxField
								name='sms'
								tx='component.managerCalendarNotifications.sms'
							/>
						</div>
					</CustomSettingsTableItem>
					<CustomSettingsTableItem label='component.managerCalendarNotifications.nextTermNotificationAt'>
						<div className={styles.inline}>
							<div className={styles.timePickerWrapper}>
								<TimePickerField
									name='notificationsInTime'
									style={{ marginBottom: 0 }}
								/>
							</div>
							<div className={styles.daysWrapper}>
								<NumberField
									name='notificationsDaysBefore'
									useWrapper={false}
								/>
							</div>
							<Text
								tx='component.managerCalendarNotifications.daysBeforeTerm'
								className={styles.suffix}
							/>
						</div>
					</CustomSettingsTableItem>
					<CustomSettingsTableItem label='component.managerCalendarNotifications.requireTermConfirmation'>
						<RadioListField
							name='requireTermConfirmation'
							inLine
							data={RADIO_OPTIONS}
						/>
					</CustomSettingsTableItem>
				</CustomSettingsTable>
				<div className='mt-3 px-3'>
					<FloatingSubmitButton
						onSubmit={handleSubmit}
						title='base.shared.save'
					/>
				</div>
			</FormWrapper>
		</div>
	);
};

export default ManagerCalendarNotifications;
